import type { ChatMessage } from './PhoneChat';

export const SMS_NUMBER = '+17209141955';
export const SMS_DISPLAY_NUMBER = '(720) 914-1955';
export const SMS_BODY = 'Hey Aspen';
export const SMS_DEEP_LINK = `sms:${SMS_NUMBER}?&body=${encodeURIComponent(SMS_BODY)}`;

// Hero Section
export const HERO_HEADLINE = 'A little conversation. A little help.';
export const HERO_SUBHEAD =
  "Chat, ask a question, study something, or think out loud with Aspen, your AI texting assistant. Reminders and check-ins are there when you ask for them.";

export const COMPLIANCE_COPY =
  'By texting Aspen, you consent to receive SMS messages from Ask Aspen. Message frequency varies. Message and data rates may apply. Reply HELP for help, STOP to opt out.';

// Trust signals under CTA. Objection killers, right where the decision happens.
export const TRUST_SIGNALS = [
  'No app to download',
  'Free to start, no credit card',
  'Cancel anytime, just text STOP',
];

// A real exchange beats any adjective we could write about one. These play as
// animated iPhone threads, and they lead with the thing no chatbot does: Aspen
// texts you first.
export const HERO_CONVERSATION: ChatMessage[] = [
  { from: 'you', text: 'what can you do?', delay: 900 },
  { from: 'aspen', text: 'We can chat, untangle an idea, or work through a study question. Try “explain why leaves change color” or tell me what’s on your mind.' },
  { from: 'you', text: 'mostly just bored lol' },
  { from: 'aspen', text: 'Tiny debate: would you rather have a soundtrack for your day or a narrator?' },
];

export const PROACTIVE_CONVERSATION: ChatMessage[] = [
  { from: 'you', text: 'Remind me to pick up dry cleaning today at 5pm. I’m in Denver.', delay: 900 },
  { from: 'aspen', text: 'I’ll remind you today at 5pm Denver time.' },
  { from: 'aspen', text: 'Your reminder: pick up dry cleaning.' },
];

export const CAPTURE_CONVERSATION: ChatMessage[] = [
  { from: 'you', text: 'trying to study but photosynthesis is a blur', delay: 700 },
  { from: 'aspen', text: 'Plants use light to turn water and carbon dioxide into sugar, releasing oxygen. Want the big picture or a practice question?' },
  { from: 'you', text: 'practice question' },
  { from: 'aspen', text: 'Which part supplies the energy: sunlight, water, or carbon dioxide?' },
];

export const MEMORY_CONVERSATION: ChatMessage[] = [
  { from: 'you', text: 'is book club this week?', delay: 700 },
  { from: 'aspen', text: 'Thursday, 7pm at Maya’s. You said you’d bring the wine this time.' },
  { from: 'you', text: 'oh no I completely forgot. remind me thursday at 5' },
  { from: 'aspen', text: 'Thursday at 5. Wine first, then Maya’s.' },
];

// How It Works section. Leads with the differentiator: they reach out to you.
export const HOW_IT_WORKS_STEPS = [
  {
    title: 'Reminders when you ask',
    description:
      'Aspen brings things back at the hour you asked for, so the thing you meant to do actually surfaces. Nothing to open, nothing to check.',
    conversation: PROACTIVE_CONVERSATION,
    conversationLabel:
      'A text thread: you request a reminder with a time and timezone, and Aspen delivers it.',
    statusTime: '5:02',
    timeLabel: 'Today 5:02 PM',
  },
  {
    title: 'Just say it, the way you’d say it',
    description:
      'Reply however you talk. Ask a question, think out loud, or hand off the thing you keep almost forgetting. Plain English, nothing to format.',
    conversation: CAPTURE_CONVERSATION,
    conversationLabel:
      'A text thread: you ask for study help and Aspen offers a short explanation and practice question.',
    statusTime: '8:12',
    timeLabel: 'Today 8:12 AM',
  },
  {
    title: 'Remember the details you confirm',
    description:
      'Aspen holds on to the details you confirm, like who Dad is, which gym, what Tuesdays look like. You start mid-conversation instead of from scratch.',
    conversation: MEMORY_CONVERSATION,
    conversationLabel:
      'A text thread: you ask about book club and Aspen already knows the night, the place, and what you promised to bring.',
    statusTime: '1:24',
    timeLabel: 'Today 1:24 PM',
  },
];

// FAQ Section
export const FAQ_ITEMS = [
  {
    question: 'Can Aspen look up current information?',
    answer: 'Yes. Aspen can search public web pages and include source links. Search is limited to three lookups per account per day and available shared capacity. If a lookup is unavailable or cannot verify an answer, Aspen will say so.',
  },
  {
    question: 'Do I need to download an app?',
    answer: `No. Text ${SMS_DISPLAY_NUMBER} and you're started. It happens right in the messages app you already use.`,
  },
  {
    question: 'Is it really free?',
    answer: 'Yes. Aspen is free to start, with no credit card and nothing to install. Heavy texters can upgrade anytime.',
  },
  {
    question: 'Will Aspen text me on their own?',
    answer:
      'Only when you have asked them to. Reminders and check-ins happen on the schedule you set, quiet hours are built in, and STOP ends them at any time.',
  },
  {
    question: 'How does Aspen understand what I mean?',
    answer:
      'Write the way you’d write a friend. “Remind me to call Dad tomorrow at 3pm” works, with no rigid syntax and no keywords to learn.',
  },
  {
    question: 'Can I stop anytime?',
    answer: 'Absolutely. Text STOP to pause product messages. Text START later when you want to resume.',
  },
  {
    question: 'Will I get spammed with texts?',
    answer:
      'No. Aspen only messages you when you asked them to. Quiet hours, pause controls, and STOP are built in.',
  },
  {
    question: 'Does it work outside the USA?',
    answer: 'Currently only USA phone numbers are supported.',
  },
  {
    question: 'What features are included?',
    answer:
      'An AI assistant for conversation, study help, and thinking out loud, memory of the details you confirm, one-time and recurring reminders, check-ins you opt into, and an optional web task list. You never need the list to text Aspen.',
  },
];
